import { parseBoundedInteger } from '../../lib/labs/fhe';

const inputA = document.querySelector<HTMLInputElement>('#fhe-a');
const inputB = document.querySelector<HTMLInputElement>('#fhe-b');
const runButton = document.querySelector<HTMLButtonElement>('#fhe-run');
const resetButton = document.querySelector<HTMLButtonElement>('#fhe-reset');
const toggleStepsButton = document.querySelector<HTMLButtonElement>('#fhe-toggle-steps');
const randomizeButton = document.querySelector<HTMLButtonElement>('#fhe-randomize-key');
const outputCipherA = document.querySelector<HTMLParagraphElement>('#fhe-cipher-a');
const outputCipherB = document.querySelector<HTMLParagraphElement>('#fhe-cipher-b');
const outputCipherSum = document.querySelector<HTMLParagraphElement>('#fhe-cipher-sum');
const outputPlainSum = document.querySelector<HTMLParagraphElement>('#fhe-plain-sum');
const outputNote = document.querySelector<HTMLParagraphElement>('#fhe-note');
const stepsPanel = document.querySelector<HTMLDivElement>('#fhe-steps');
const stepRa = document.querySelector<HTMLSpanElement>('#fhe-step-ra');
const stepGa = document.querySelector<HTMLSpanElement>('#fhe-step-ga');
const stepRna = document.querySelector<HTMLSpanElement>('#fhe-step-rna');
const stepRb = document.querySelector<HTMLSpanElement>('#fhe-step-rb');
const stepGb = document.querySelector<HTMLSpanElement>('#fhe-step-gb');
const stepRnb = document.querySelector<HTMLSpanElement>('#fhe-step-rnb');
const outputP = document.querySelector<HTMLParagraphElement>('#fhe-p');
const outputQ = document.querySelector<HTMLParagraphElement>('#fhe-q');
const outputN = document.querySelector<HTMLParagraphElement>('#fhe-n');
const outputNSquared = document.querySelector<HTMLParagraphElement>('#fhe-n-squared');
const outputG = document.querySelector<HTMLParagraphElement>('#fhe-g');
const outputLambda = document.querySelector<HTMLParagraphElement>('#fhe-lambda');
const outputMu = document.querySelector<HTMLParagraphElement>('#fhe-mu');

if (
  !inputA ||
  !inputB ||
  !runButton ||
  !resetButton ||
  !toggleStepsButton ||
  !randomizeButton ||
  !outputCipherA ||
  !outputCipherB ||
  !outputCipherSum ||
  !outputPlainSum ||
  !outputNote ||
  !stepsPanel ||
  !stepRa ||
  !stepGa ||
  !stepRna ||
  !stepRb ||
  !stepGb ||
  !stepRnb ||
  !outputP ||
  !outputQ ||
  !outputN ||
  !outputNSquared ||
  !outputG ||
  !outputLambda ||
  !outputMu
) {
  throw new Error('FHE demo elements not found.');
}

type PaillierKey = {
  p: bigint;
  q: bigint;
  n: bigint;
  nSquared: bigint;
  g: bigint;
  lambda: bigint;
  mu: bigint;
};

function gcd(a: bigint, b: bigint): bigint {
  let x = a;
  let y = b;
  while (y !== 0n) {
    const temp = x % y;
    x = y;
    y = temp;
  }
  return x;
}

function lcm(a: bigint, b: bigint): bigint {
  return (a / gcd(a, b)) * b;
}

function modPow(base: bigint, exponent: bigint, modulus: bigint): bigint {
  let result = 1n;
  let b = base % modulus;
  let e = exponent;
  while (e > 0n) {
    if (e & 1n) {
      result = (result * b) % modulus;
    }
    e >>= 1n;
    b = (b * b) % modulus;
  }
  return result;
}

function modInv(value: bigint, modulus: bigint): bigint {
  let a = value;
  let m = modulus;
  let [x0, x1] = [0n, 1n];
  while (a > 1n) {
    const q = a / m;
    [a, m] = [m, a % m];
    [x0, x1] = [x1 - q * x0, x0];
  }
  if (x1 < 0n) {
    x1 += modulus;
  }
  return x1;
}

function isPrime(value: number): boolean {
  if (value < 2) return false;
  if (value % 2 === 0) return value === 2;
  for (let i = 3; i * i <= value; i += 2) {
    if (value % i === 0) return false;
  }
  return true;
}

const PRIME_MIN = 200;
const PRIME_MAX = 1000;
const PRIME_POOL: bigint[] = [];
for (let i = PRIME_MIN; i <= PRIME_MAX; i += 1) {
  if (isPrime(i)) {
    PRIME_POOL.push(BigInt(i));
  }
}

function randomPrime(exclude?: bigint): bigint {
  if (PRIME_POOL.length < 2) {
    throw new Error('Prime pool too small for demo.');
  }
  let candidate: bigint;
  do {
    const value = crypto.getRandomValues(new Uint32Array(1))[0];
    candidate = PRIME_POOL[value % PRIME_POOL.length];
  } while (exclude && candidate === exclude);
  return candidate;
}

function generateToyKey(): PaillierKey {
  const p = randomPrime();
  const q = randomPrime(p);
  const n = p * q;
  const nSquared = n * n;
  const g = n + 1n;
  const lambda = lcm(p - 1n, q - 1n);
  const u = modPow(g, lambda, nSquared);
  const lValue = (u - 1n) / n;
  const mu = modInv(lValue % n, n);
  return { p, q, n, nSquared, g, lambda, mu };
}

function randomCoprime(n: bigint): bigint {
  const max = Number(n - 2n);
  let candidate: bigint;
  do {
    const value = crypto.getRandomValues(new Uint32Array(1))[0] % max;
    candidate = BigInt(value) + 1n;
  } while (gcd(candidate, n) !== 1n);
  return candidate;
}

function encryptWithSteps(value: bigint, key: PaillierKey) {
  const r = randomCoprime(key.n);
  const part1 = modPow(key.g, value, key.nSquared);
  const part2 = modPow(r, key.n, key.nSquared);
  return {
    ciphertext: (part1 * part2) % key.nSquared,
    r,
    part1,
    part2,
  };
}

function decrypt(ciphertext: bigint, key: PaillierKey): bigint {
  const u = modPow(ciphertext, key.lambda, key.nSquared);
  const lValue = (u - 1n) / key.n;
  return (lValue * key.mu) % key.n;
}

let key = generateToyKey();

function renderKey() {
  outputP.textContent = key.p.toString();
  outputQ.textContent = key.q.toString();
  outputN.textContent = key.n.toString();
  outputNSquared.textContent = key.nSquared.toString();
  outputG.textContent = key.g.toString();
  outputLambda.textContent = key.lambda.toString();
  outputMu.textContent = key.mu.toString();
}

function runDemo() {
  const a = parseBoundedInteger(inputA.value, 0, 1000);
  const b = parseBoundedInteger(inputB.value, 0, 1000);
  if (a === null || b === null) {
    outputNote.textContent = 'Enter two whole numbers between 0 and 1000.';
    return;
  }

  const aBig = BigInt(a);
  const bBig = BigInt(b);
  const encryptedA = encryptWithSteps(aBig, key);
  const encryptedB = encryptWithSteps(bBig, key);
  const cipherA = encryptedA.ciphertext;
  const cipherB = encryptedB.ciphertext;
  const cipherSum = (cipherA * cipherB) % key.nSquared;
  const plainSum = decrypt(cipherSum, key);

  outputCipherA.textContent = cipherA.toString();
  outputCipherB.textContent = cipherB.toString();
  outputCipherSum.textContent = cipherSum.toString();
  outputPlainSum.textContent = plainSum.toString();
  stepRa.textContent = encryptedA.r.toString();
  stepGa.textContent = encryptedA.part1.toString();
  stepRna.textContent = encryptedA.part2.toString();
  stepRb.textContent = encryptedB.r.toString();
  stepGb.textContent = encryptedB.part1.toString();
  stepRnb.textContent = encryptedB.part2.toString();
  outputNote.textContent =
    'Server sum = ciphertext A * ciphertext B mod n^2. Your browser decrypted the sum.';
}

function randomInt(min: number, max: number): number {
  return min + (crypto.getRandomValues(new Uint32Array(1))[0] % (max - min + 1));
}

function resetDemo() {
  inputA.value = String(randomInt(1, 500));
  inputB.value = String(randomInt(1, 500));
  outputCipherA.textContent = '-';
  outputCipherB.textContent = '-';
  outputCipherSum.textContent = '-';
  outputPlainSum.textContent = '-';
  stepRa.textContent = '';
  stepGa.textContent = '';
  stepRna.textContent = '';
  stepRb.textContent = '';
  stepGb.textContent = '';
  stepRnb.textContent = '';
  outputNote.textContent = 'Pick values and run the demo.';
}

runButton.addEventListener('click', runDemo);
resetButton.addEventListener('click', resetDemo);
toggleStepsButton.addEventListener('click', () => {
  const isHidden = stepsPanel.classList.toggle('hidden');
  toggleStepsButton.textContent = isHidden
    ? 'Show intermediate steps'
    : 'Hide intermediate steps';
});
randomizeButton.addEventListener('click', () => {
  key = generateToyKey();
  renderKey();
  resetDemo();
  outputNote.textContent = 'New toy key ready. Pick values and run the demo.';
});

renderKey();
resetDemo();
